import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { CalendarDays, Navigation } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTrip } from '../trips/useTrip'
import { usePoints } from '../timeline/usePoints'
import { Spinner } from '../../shared/components/Spinner'
import { EmptyState } from '../../shared/components/EmptyState'
import { Button } from '../../shared/components/Button'
import { GeoapifyMap, type MapMarker } from '../../shared/components/GoogleMap'
import { gmapsRouteLink } from '../../shared/lib/gmapsLink'
import type { TripPoint } from '../../shared/api/types'

export function MapView() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const { data: trip } = useTrip(id)
  const { data: points = [], isLoading } = usePoints(id)
  const located = useMemo(() => points.filter((p): p is TripPoint & { lat: number; lng: number } =>
    p.lat != null && p.lng != null).sort((a,b) => a.day_index-b.day_index || a.order-b.order), [points])
  const markers = useMemo<MapMarker[]>(() => located.map(p => ({ id: p.id, lat: p.lat, lng: p.lng,
    title: p.title, subtitle: `${t('calendar.day')} ${p.day_index + 1} · ${p.address}`, dayIndex: p.day_index })), [located, t])
  if (isLoading) return <Spinner label={t('map.loading')} />
  return <div className="mx-auto max-w-5xl">
    <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
      <div><h1 className="font-display text-2xl font-bold">{t('map.title')}</h1>
        <p className="text-sm text-st-muted">{trip?.title}</p></div>
      {located.length > 0 && <a href={gmapsRouteLink(located)} target="_blank" rel="noreferrer">
        <Button variant="outline"><Navigation className="h-4 w-4" /> {t('map.open_google')}</Button></a>}
    </div>
    {located.length === 0 ? <EmptyState title={t('map.empty')} description={t('map.empty_description')}
      action={<Link to={`/trips/${id}/places`}><Button variant="outline"><CalendarDays className="h-4 w-4" /> {t('map.add_places')}</Button></Link>} />
      : <div className="overflow-hidden rounded-st-lg"><GeoapifyMap markers={markers} lines /></div>}
  </div>
}
